import React from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

function fmt(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `£${(n / 1_000).toFixed(0)}k`;
  return `£${n}`;
}

export default function PayrollSummary({ loading, data, kpis }) {
  const rawTrend = data?.trend || [];

  // trend can be array of numbers or [{month, amount}]
  const trendData = rawTrend.map((v, i) => typeof v === "number"
    ? { month: `M-${rawTrend.length - i}`, amount: v }
    : v
  );

  if (loading) return (
    <div className={`${card} p-5`}>
      <div className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Payroll Summary</div>
      <div className="h-48 rounded-lg bg-white/5 animate-pulse" />
    </div>
  );

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Payroll Summary</span>
      </div>
      <div className="p-5 space-y-4">

        {/* Stat chips */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "This Month",   value: fmt(kpis?.payrollThisMonth), color: "#00ff90"              },
            { label: "MoM Change",   value: kpis?.payrollVarPct != null ? `${kpis.payrollVarPct > 0 ? "+" : ""}${kpis.payrollVarPct}%` : "—", color: (kpis?.payrollVarPct ?? 0) >= 0 ? "#34d399" : "#f87171" },
            { label: "Avg Cost/FTE", value: fmt(kpis?.avgCostFte),       color: "#60a5fa"              },
          ].map(({ label, value, color }) => (
            <div key={label} className="rounded-lg border border-white/5 bg-white/3 p-3 text-center">
              <p className="text-sm font-bold" style={{ color }}>{value}</p>
              <p className="text-xs text-white/30 mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        {/* Trend area chart */}
        {trendData.length > 1 && (
          <div>
            <p className="text-xs text-white/30 uppercase tracking-wider mb-2">Payroll Trend (Last {trendData.length} Months)</p>
            <ResponsiveContainer width="100%" height={120}>
              <AreaChart data={trendData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="month" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }}
                  tickFormatter={v => `£${(v/1000).toFixed(0)}k`} width={42} />
                <Tooltip {...tooltipStyle} formatter={v => [fmt(v), "Payroll"]} />
                <Area type="monotone" dataKey="amount" stroke="#00ff90" fill="#00ff9018" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Dept breakdown if available */}
        {data?.byDept?.length > 0 && (
          <div>
            <p className="text-xs text-white/30 uppercase tracking-wider mb-2">By Department</p>
            <div className="space-y-1.5">
              {data.byDept.map((d, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-white/50">{d.dept}</span>
                  <span className="text-white/70 font-medium">{fmt(d.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}